import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Empty, Input, Row, Space, Tag, theme } from 'antd';
import Title from 'antd/lib/typography/Title';
import Paragraph from 'antd/lib/typography/Paragraph';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, ArrowRightOutlined, ClockCircleOutlined } from '@ant-design/icons';
import Page from '../../Components/Page/Page';
import BookyBox from '../../Components/Layout/BookyBox';
import { useAuth } from '../../Contexts/AuthContext';
import { AuthProvider } from '../../Providers/authProvider';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { HelpArticle, helpArticles, helpSections } from './HelpContent';
import './Help.css';

const pathMarkup = /\[\[(.+?)\]\]/g;

const searchWords = (search: string) => search.toLowerCase().split(/\s+/).filter(w => w.length > 0);

// Every word has to match, so "betala faktura" narrows instead of widening.
const containsAll = (texts: string[], words: string[]) => {
    const haystack = texts.join(" ").replace(pathMarkup, "$1").toLowerCase();
    return words.every(word => haystack.includes(word));
};

const matchesSearch = (article: HelpArticle, search: string) =>
    containsAll([article.question, article.intro, ...(article.steps ?? []), ...(article.notes ?? []), ...(article.keywords ?? [])], searchWords(search));

// What the guide is about (question and keywords) outranks a word that just happens to be in the body text.
const matchesTopic = (article: HelpArticle, search: string) =>
    containsAll([article.question, ...(article.keywords ?? [])], searchWords(search));

// Renders "[[Inställningar > Medlemsformulär]]" as a label, see HelpContent.ts.
const renderText = (text: string) => text.split(pathMarkup).map((part, i) =>
    i % 2 === 1
        ? <Tag key={i} color="blue" className="help-path">{part.split(">").map(p => p.trim()).join(" › ")}</Tag>
        : part
);

const articleFromHash = (hash: string) => helpArticles.find(a => a.id === decodeURIComponent(hash.replace("#", "")));

const Help = () => {
    const { Panel } = Collapse;
    const { user } = useAuth();
    const { token } = theme.useToken();
    const location = useLocation();
    const navigate = useNavigate();

    const [search, setSearch] = useState("");
    const [sectionId, setSectionId] = useState(undefined as string | undefined);
    const [openArticleId, setOpenArticleId] = useState(undefined as string | undefined);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    // The help center is for logged in administrators only. The hash is kept in the redirect so a
    // link to a specific guide still opens that guide after login.
    useEffect(() => {
        AuthProvider().GetLoggedInUser().then(loggedInUser => {
            if (loggedInUser === null) {
                window.location.href = "/Login?redirect=" + encodeURIComponent(window.location.pathname + window.location.hash);
                return;
            }
            setIsLoggedIn(true);
        });
    }, []);

    // /help#article-id opens that question directly, so support can answer with a link.
    useEffect(() => {
        const article = articleFromHash(location.hash);
        if (article) {
            setSearch("");
            setSectionId(article.sectionId);
            setOpenArticleId(article.id);
        }
    }, [location.hash]);

    const isSearching = search.trim().length > 0;
    const section = helpSections.find(s => s.id === sectionId);

    const visibleArticles = useMemo(() => {
        // A search looks through all sections - the chosen section only applies when browsing.
        if (!isSearching) {
            return helpArticles.filter(a => a.sectionId === sectionId);
        }

        const matches = helpArticles.filter(a => matchesSearch(a, search));
        return [...matches.filter(a => matchesTopic(a, search)), ...matches.filter(a => !matchesTopic(a, search))];
    }, [search, isSearching, sectionId]);

    const onArticleChange = (key: string | string[]) => {
        const id = Array.isArray(key) ? key[0] : key;
        setOpenArticleId(id);
        navigate({ hash: id ? "#" + id : "" }, { replace: true });
    };

    const showSection = (id?: string) => {
        setSectionId(id);
        setSearch("");
        setOpenArticleId(undefined);
        navigate({ hash: "" }, { replace: true });
    };

    const sectionCards = <Row gutter={[16, 16]}>
        {helpSections.map(s => {
            const Icon = s.icon;
            const count = helpArticles.filter(a => a.sectionId === s.id).length;
            return <Col key={s.id} xs={24} sm={12} md={8}>
                <Card hoverable className="help-section-card" data-testid={"help-section-" + s.id} onClick={() => showSection(s.id)}>
                    <Icon className="help-section-icon" style={{ color: token.colorPrimary }} />
                    <Title level={4}>{s.title}</Title>
                    <Paragraph>{s.description}</Paragraph>
                    <span className="help-section-count">{count} {count === 1 ? "guide" : "guider"}</span>
                </Card>
            </Col>;
        })}
    </Row>;

    const articleList = visibleArticles.length === 0
        ? <Empty description="Inga guider matchade din sökning." />
        : <Collapse accordion activeKey={openArticleId} onChange={onArticleChange}>
            {visibleArticles.map(article =>
                <Panel
                    header={article.question}
                    key={article.id}
                    // The site theme renders the default (colorless) Tag black-on-black, so always give it a color.
                    extra={isSearching ? <Tag color="blue">{helpSections.find(s => s.id === article.sectionId)?.title}</Tag> : undefined}
                >
                    {article.minutes ? <div className="help-meta"><ClockCircleOutlined /> ca {article.minutes} min</div> : ""}
                    <Paragraph>{renderText(article.intro)}</Paragraph>
                    {article.steps ? <ol className="help-steps">
                        {article.steps.map((step, i) => <li key={i}>
                            <span className="help-step-number" style={{ background: token.colorPrimary }}>{i + 1}</span>
                            <span>{renderText(step)}</span>
                        </li>)}
                    </ol> : ""}
                    {article.notes?.map((note, i) => <Alert key={i} className="help-note" type="info" showIcon message={renderText(note)} />)}
                    {user != null && article.links ? <Space wrap className="help-actions">
                        {article.links.map((link, i) =>
                            <Link key={link.path} to={"/" + getCurrentLanguage() + "/" + link.path}>
                                <Button type={i === 0 ? "primary" : "default"}>{link.label} <ArrowRightOutlined /></Button>
                            </Link>)}
                    </Space> : ""}
                </Panel>
            )}
        </Collapse>;

    if (!isLoggedIn) {
        return <Page pageTitle="Hjälp | OneFront360"></Page>;
    }

    return <Page pageTitle="Hjälp | OneFront360">
        <BookyBox>
            <div className="help-hero">
                <Title>Vad vill du göra?</Title>
                <Input.Search
                    data-testid="help-search"
                    allowClear
                    size="large"
                    placeholder="Sök, t.ex. &quot;betala&quot; eller &quot;hemsida&quot;"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            {!isSearching && !section ? sectionCards : <>
                <Button type="link" icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0 }} onClick={() => showSection(undefined)}>Alla områden</Button>
                <Title level={3}>{isSearching ? "Sökresultat" : section?.title}</Title>
                {articleList}
            </>}

            <Paragraph style={{ marginTop: "24px", textAlign: "center" }}>
                Hittar du inte svaret? <Link to={"/" + getCurrentLanguage() + "/contact"}>Kontakta supporten</Link>.
            </Paragraph>
        </BookyBox>
    </Page>;
};

export default Help;
